import {
  AbsoluteCenter,
  Box,
  Flex,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  getCustomerBusinessInfoApi,
  upsertCustomerBusinessInfoApi,
} from 'api/underwritingApi';
import {
  getCustomerBillingDetailsApi,
  upsertCustomerBillingDetailsApi,
} from 'api/accountingInternalApi';
import { getCustomerProfilesApi } from 'api/adminApi';
import Card from 'components/card/Card';
import moment from 'moment';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import EditCustomerSettings from './EditCustomerSettings';
import CustomerCockpitProfileCard from './CustomerCockpitProfileCard';
import CustomerBillingSettingsCard from './CustomerBillingSettingsCard';
import CustomerKycCard from './CustomerKycCard';
import CustomerMarketingViewsCard from './CustomerMarketingViewsCard';
import { mergeBillingDetails, normalizeBillingDetails } from 'pages/Cockpit/AccountingInternal/calc/billingDetails';

import { TwDatePicker } from 'components/DatePicker';
import { currencyFormat } from 'components/Number';
import { CurrencyNumberInput, TwNumberInput } from 'components/NumberInput';
import SelectInput from 'components/inputs/SelectInput';
import {
  BUSINESS_MODEL_TYPE_META,
  BUSINESS_PAYMENT_SERVICE_TYPE_META,
  BUSINESS_PLATFORM_TYPE_META,
} from 'constants/underwritingConstants';
import { CockpitCustomerContext } from 'context/CockpitCustomerContext';
import { generateSelectOptions } from 'helpers/inputHelper';
import validator from 'validator';

const fields = [
  {
    label: 'Legal Name',
    name: 'legalName',
    element: Input,
    section: 1,
  },
  {
    label: 'HQ',
    name: 'headquarters',
    element: Input,
    section: 1,
  },
  {
    label: 'Founded',
    name: 'foundedDate',
    element: TwDatePicker,
    section: 1,
    formatValue: (value) => {
      return moment(value).format('MMMM DD, YYYY');
    },
  },
  {
    label: 'Employees',
    name: 'numberOfEmployees',
    element: TwNumberInput,
    section: 1,
  },
  {
    label: 'Founders / mgmt.',
    name: 'founders',
    element: Input,
    section: 1,
  },
  {
    label: 'Business Model',
    name: 'model',
    element: SelectInput,
    placeholder: 'Select Business Model',
    options: generateSelectOptions({ metaObj: BUSINESS_MODEL_TYPE_META }),
    section: 2,
    formatValue: (value) => {
      return BUSINESS_MODEL_TYPE_META[value]?.label;
    },
  },
  {
    label: 'Business Start Date',
    name: 'businessStartDate',
    element: TwDatePicker,
    section: 2,
    formatValue: (value) => {
      return moment(value).format('MMMM DD, YYYY');
    },
  },
  {
    label: 'Product',
    name: 'productName',
    element: Input,
    section: 2,
  },
  {
    label: 'Platforms Mix',
    name: 'platformTypes',
    options: generateSelectOptions({
      metaObj: BUSINESS_PLATFORM_TYPE_META,
    }),
    element: SelectInput,
    isMulti: true,
    placeholder: 'Select Platforms',
    section: 2,
    formatValue: (value) => {
      return value
        ?.map((platform) => {
          return BUSINESS_PLATFORM_TYPE_META[platform]?.label;
        })
        .join(', ');
    },
  },
  {
    label: 'Payment Processor',
    name: 'paymentServices',
    element: SelectInput,
    placeholder: 'Select Payment Processor',
    options: generateSelectOptions({
      metaObj: BUSINESS_PAYMENT_SERVICE_TYPE_META,
    }),
    isMulti: true,
    section: 3,
    formatValue: (value) => {
      return value
        .map((service) => {
          return BUSINESS_PAYMENT_SERVICE_TYPE_META[service]?.label;
        })
        .join(', ');
    },
  },
  {
    label: 'Raised to Date',
    name: 'totalRaisedAmount',
    element: CurrencyNumberInput,
    section: 3,
    formatValue: (value) => currencyFormat(value),
  },
  {
    label: 'Last Round',
    name: 'lastRoundDate',
    element: TwDatePicker,
    section: 3,
    formatValue: (value) => {
      return moment(value).format('MMMM DD, YYYY');
    },
  },
  {
    label: 'VC Tier',
    name: 'vcTier',
    element: TwNumberInput,
    section: 3,
  },
  {
    label: 'Fundraising History',
    name: 'fundraisingHistory',
    element: Input,
    section: 3,
  },
  {
    label: 'Notable Investors',
    name: 'notableInvestors',
    element: Input,
    section: 3,
  },
  {
    label: 'Investor Score',
    name: 'investorScore',
    element: SelectInput,
    placeholder: 'Select Score',
    options: [
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
    ],
    isNumber: true,
    tooltip: '3 - highest, 1 - lowest',
    section: 4,
  },
  {
    label: 'Management Score',
    name: 'managementScore',
    element: SelectInput,
    placeholder: 'Select Score',
    options: [
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
    ],
    isNumber: true,
    tooltip: '3 - highest, 1 - lowest',
    section: 4,
  },
  {
    label: 'External ID',
    name: 'externalId',
    element: Input,
    section: 5,
  },
  {
    label: 'Allowed Emails',
    name: 'allowedEmails',
    element: Input,
    section: 6,
    tooltip: 'Comma-separated list of allowed emails (optional)',
    placeholder: 'email1@example.com, email2@example.com',
    validate: (value) => {
      if (!value || !value.trim()) return true;
      const invalidEmail = value
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean)
        .find((email) => !validator.isEmail(email));
      return invalidEmail ? `Invalid email: ${invalidEmail}` : true;
    },
  },
];

const FieldsSection = ({ fields, data }) => {
  return fields.map((field) => {
    let value = data?.[field.name];
    if (!!value && field.formatValue) {
      value = field.formatValue(value);
    }
    return (
      <Fragment key={field.name}>
        <Text color={'secondaryGray.600'} fontWeight={500}>
          {field.label}
        </Text>
        <Text>{value}</Text>
      </Fragment>
    );
  });
};

const CustomerSettings = (props) => {
  const [businessInfo, setBusinessInfo] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [customerTenant, setCustomerTenant] = useState('Twelve');
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { customerId } = useParams();
  const { updateBusinessModel, customer } = useContext(CockpitCustomerContext);

  useEffect(() => {
    Promise.all([getBusinessInfo(), loadCustomerTenant()]);
  }, [customerId, customer?.companyName]);

  const loadCustomerTenant = async () => {
    try {
      const profiles = await getCustomerProfilesApi();
      const profile = profiles.find((p) => p.id === customerId);
      if (profile) {
        setCustomerTenant(profile.tenant || 'Twelve');
      }
    } catch (error) {
      // non-critical, silently ignore
    }
  };

  const getBusinessInfo = async () => {
    setLoading(true);
    try {
      const _businessInfo = await getCustomerBusinessInfoApi({
        customerId,
      });
      if (_businessInfo) {
        if (_businessInfo.investorScore == null) _businessInfo.investorScore = 2;
        if (_businessInfo.managementScore == null) _businessInfo.managementScore = 2;
      }
      setBusinessInfo(_businessInfo);
      const apiBilling = await getCustomerBillingDetailsApi({ customerId }).catch(() => null);
      const storedBilling =
        apiBilling?.invoiceCompanyName || apiBilling?.billingEmail ? apiBilling : null;
      setBillingDetails(
        mergeBillingDetails({
          businessInfo: _businessInfo,
          companyName: customer?.companyName,
          storedBilling,
          customerUsers: customer?.users,
          allowedEmails: _businessInfo?.allowedEmails,
        })
      );
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load customer settings',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveBusinessInfo = async (_data) => {
    setLoading(true);
    try {
      await upsertCustomerBusinessInfoApi({
        customerId,
        data: _data,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save customer settings',
        status: 'error',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (_data) => {
    await saveBusinessInfo(_data);
    if (_data.model && businessInfo?.model != _data.model) {
      updateBusinessModel(_data.model);
    }
    setBusinessInfo(_data);
  };

  const onSaveBillingDetails = async (_data) => {
    const normalized = normalizeBillingDetails(_data, billingDetails);
    try {
      await upsertCustomerBillingDetailsApi({
        customerId,
        billingDetails: normalized,
      });
      setBillingDetails(normalized);
      toast({
        title: 'Billing details saved',
        status: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save billing details',
        status: 'error',
      });
    }
  };

  if (loading) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  return (
    <Box minW={'container.lg'} maxW={'max'}>
      <Flex justifyContent={'end'}>
        <EditCustomerSettings
          fields={fields}
          onSave={onSave}
          data={businessInfo}
        />
      </Flex>
      <Card>
        <Heading variant={'sectionHeader'} borderColor={'secondaryGray.600'}>
          Customer settings
        </Heading>
        <SimpleGrid
          gridTemplateColumns={'min-content min-content'}
          whiteSpace={'nowrap'}
          spacingY={0}
          spacingX={10}
          fontSize={'sm'}
          mt={45}
        >
          <Text color={'secondaryGray.600'} fontWeight={500}>
            Tenant
          </Text>
          <Text fontWeight={800}>{customerTenant}</Text>
          <Box>&nbsp;</Box>
          <Box>&nbsp;</Box>
          <FieldsSection
            fields={fields.filter((field) => field.section === 1)}
            data={businessInfo}
          />
          <Box>&nbsp;</Box>
          <Box>&nbsp;</Box>
          <FieldsSection
            fields={fields.filter((field) => field.section === 2)}
            data={businessInfo}
          />
          <Box>&nbsp;</Box>
          <Box>&nbsp;</Box>
          <FieldsSection
            fields={fields.filter((field) => field.section === 3)}
            data={businessInfo}
          />
          <Box>&nbsp;</Box>
          <Box>&nbsp;</Box>
          <FieldsSection
            fields={fields.filter((field) => field.section === 4)}
            data={businessInfo}
          />
        </SimpleGrid>
      </Card>
      <CustomerBillingSettingsCard
        billingDetails={billingDetails}
        onSave={onSaveBillingDetails}
      />
      <CustomerKycCard customerId={customerId} toast={toast} />
      <CustomerMarketingViewsCard customerId={customerId} />
      <CustomerCockpitProfileCard customerId={customerId} toast={toast} />
    </Box>
  );
};

CustomerSettings.propTypes = {};

export default CustomerSettings;
